import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { fetchProduct, fetchProducts, addViewHistory, addToCart } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { waProductLink } from "@/lib/whatsapp";
import { ArrowLeft, Check, ShieldCheck, Truck, BadgeCheck, ShoppingCart, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import ProductCard from "@/components/ProductCard";
import { resolveImg } from "@/components/admin/ProductsTab";

export default function ProductDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const [product, setProduct] = useState(null);
  const [activeImg, setActiveImg] = useState(0);
  const [activeSize, setActiveSize] = useState(null);
  const [related, setRelated] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [addingToCart, setAddingToCart] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    setLoading(true);
    setNotFound(false);
    setActiveImg(0);
    setActiveSize(null);
    fetchProduct(id)
      .then(async (p) => {
        setProduct(p);
        if (p?.sizes?.length) setActiveSize(p.sizes[0]);
        try {
          const rel = await fetchProducts({ category: p.category });
          setRelated(rel.filter((r) => r.id !== p.id).slice(0, 4));
        } catch {}
        // Log view history if user is logged in
        if (user) {
          try { addViewHistory(user.id, p.id); } catch {}
        }
      })
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [id]);

  if (loading) {
    return <div className="max-w-7xl mx-auto px-5 md:px-8 py-20 text-center font-display text-2xl" data-testid="product-loading">Loading…</div>;
  }
  if (notFound || !product) {
    return (
      <div className="max-w-7xl mx-auto px-5 md:px-8 py-20 text-center" data-testid="product-not-found">
        <h2 className="font-display font-black text-4xl mb-4">Oops! Product not found</h2>
        <Link to="/shop" className="nb-btn bg-[var(--primary)] text-white inline-flex items-center gap-2"><ArrowLeft strokeWidth={3} /> Back to Shop</Link>
      </div>
    );
  }

  const message = `Hi Khasim Trendz! I'm interested in *${product.name}* (₹${product.price})${activeSize ? ` — Size ${activeSize}` : ""}. Is it available?`;
  const wa = `https://wa.me/919885544389?text=${encodeURIComponent(message)}`;

  const handleAddToCart = async () => {
    if (!user) {
      toast.error("Please login to add items to your cart.");
      navigate("/login");
      return;
    }
    setAddingToCart(true);
    try {
      await addToCart(user.id, product.id);
      toast.success(`${product.name} added to cart!`);
    } catch (err) {
      toast.error(err.message || "Failed to add to cart");
    } finally {
      setAddingToCart(false);
    }
  };

  return (
    <div data-testid="product-detail-page">
      <div className="max-w-7xl mx-auto px-5 md:px-8 py-8">
        <Link to={`/shop/${product.category}`} className="inline-flex items-center gap-2 nb-pill mb-6" data-testid="back-to-shop">
          <ArrowLeft size={16} strokeWidth={3} /> Back to {product.category.replace("-", " ")}
        </Link>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
          {/* Gallery */}
          <div>
            <div className="relative aspect-[4/5] nb-card overflow-hidden bg-[var(--candy-blue)]">
              <img
                src={resolveImg(product.images?.[activeImg])}
                alt={product.name}
                className="w-full h-full object-cover"
              />
              {product.badge && (
                <span className="absolute top-4 left-4 bg-[var(--primary)] text-white px-3 py-1 text-sm font-black uppercase rounded-full nb-border" style={{ boxShadow: "3px 3px 0 0 #0A0A0A" }}>
                  {product.badge}
                </span>
              )}
            </div>
            {product.images?.length > 1 && (
              <div className="grid grid-cols-4 gap-3 mt-4">
                {product.images.map((img, i) => (
                  <button
                    key={i}
                    data-testid={`thumb-${i}`}
                    onClick={() => setActiveImg(i)}
                    className={`aspect-square rounded-2xl border-[3px] border-black overflow-hidden ${activeImg === i ? "shadow-[4px_4px_0_0_#FF3366]" : ""}`}
                  >
                    <img src={resolveImg(img)} alt={`${product.name} ${i+1}`} className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Details */}
          <div>
            <div className="nb-pill inline-block mb-4 capitalize">{product.category.replace("-", " ")}</div>
            <h1 className="font-display font-black uppercase text-4xl md:text-6xl tracking-tight leading-[0.95]">{product.name}</h1>
            <div className="mt-5 flex items-baseline gap-3">
              <span className="font-display font-black text-4xl md:text-5xl text-[var(--primary)]">₹{product.price}</span>
              {product.original_price && product.original_price > product.price && (
                <>
                  <span className="text-xl line-through text-black/40">₹{product.original_price}</span>
                  <span className="bg-[var(--secondary)] text-black px-2 py-1 text-xs font-black uppercase rounded-full nb-border">
                    {Math.round(((product.original_price - product.price) / product.original_price) * 100)}% OFF
                  </span>
                </>
              )}
            </div>

            <p className="mt-6 text-lg text-black/80 max-w-xl">{product.description}</p>

            {product.sizes?.length > 0 && (
              <div className="mt-7">
                <div className="text-sm font-black uppercase tracking-wider mb-3">Choose Size</div>
                <div className="flex flex-wrap gap-2">
                  {product.sizes.map((s) => (
                    <button
                      key={s}
                      data-testid={`size-${s}`}
                      onClick={() => setActiveSize(s)}
                      className={`nb-pill min-w-[52px] ${activeSize === s ? "bg-[var(--primary)] text-white" : ""}`}
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {product.colors?.length > 0 && (
              <div className="mt-6">
                <div className="text-sm font-black uppercase tracking-wider mb-3">Available Colors</div>
                <div className="flex flex-wrap gap-2">
                  {product.colors.map((c) => (
                    <span key={c} className="nb-pill" data-testid={`color-${c}`}>{c}</span>
                  ))}
                </div>
              </div>
            )}

            <div className="mt-8 flex flex-wrap gap-3">
              <button
                onClick={handleAddToCart}
                disabled={addingToCart}
                className="nb-btn bg-black text-white inline-flex items-center gap-2 disabled:opacity-70"
              >
                {addingToCart ? <Loader2 size={20} className="animate-spin" /> : <ShoppingCart size={20} />}
                Add to Cart
              </button>
              <a
                href={wa}
                target="_blank"
                rel="noreferrer"
                data-testid="product-buy-wa-btn"
                className="nb-btn bg-[var(--whatsapp)] text-white inline-flex items-center gap-2"
              >
                Buy on WhatsApp
              </a>
              <Link to="/contact" className="nb-btn bg-white text-black">Visit Store</Link>
            </div>

            <div className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-3">
              {[
                { icon: <BadgeCheck strokeWidth={2.5} />, t: "Authentic Pieces" },
                { icon: <Truck strokeWidth={2.5} />, t: "Quick Local Delivery" },
                { icon: <ShieldCheck strokeWidth={2.5} />, t: "Try & Buy at Store" },
              ].map((u, i) => (
                <div key={i} className="nb-pill !w-full justify-start flex items-center gap-2 !py-3">
                  {u.icon} <span>{u.t}</span>
                </div>
              ))}
            </div>

            <div className="mt-8 nb-card p-5 bg-[var(--secondary)]">
              <div className="flex items-start gap-3">
                <Check strokeWidth={3} />
                <p className="font-bold">Tap "Buy on WhatsApp" — we'll confirm stock, share more pics, and arrange delivery or pickup at our Buttaipet store.</p>
              </div>
            </div>
          </div>
        </div>

        {/* Related */}
        {related.length > 0 && (
          <section className="mt-20" data-testid="related-section">
            <h2 className="font-display font-black uppercase text-3xl md:text-5xl mb-8">You may also like</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
              {related.map((p, i) => (
                <ProductCard key={p.id} product={p} index={i} />
              ))}
            </div>
          </section>
        )}
      </div>
    </div>
  );
}
